import {
  getWorkflowRuleGroups,
  toWorkflowRuleGroupsPayload,
  workflowRuleGroupsSchema,
  canPreviewWorkflowRuleGroups,
} from "./workflow-rule-groups.ts";
import { apiFiles } from "@/api/file.api.ts";
import { RuleGroupsEditor } from "@/components/apply-to/RuleGroupsEditor.tsx";
import { FileItem } from "@/components/files/FileItem.tsx";
import { FileUpload } from "@/components/files/FileUpload.tsx";
import { InputWithVariableInsert } from "@/components/input-with-variable-insert/InputWithVariableInsert.tsx";
import { RichEditor } from "@/components/rich-editor/RichEditor.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { WorkflowReminderSettingsSection } from "@/pages/workflows/edit/actions/WorkflowReminderSettingsSection.tsx";
import { useWorkflowBlockConditions } from "@/pages/workflows/edit/flow/use-workflow-block-conditions.tsx";
import { WorkflowBlockConditionsList } from "@/pages/workflows/edit/flow/WorkflowBlockConditionsList.tsx";
import { useWorkflowUpdateBlockActionTask } from "@/queries/workflows/workflow.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod } from "@/types/reminder.types.ts";
import { Rule, RuleApplySignType, RuleConditionType } from "@/types/role.types.ts";
import { ApiRuleConditionTypeInfo, RuleConditionContext, RuleGroup } from "@/types/rule.types.ts";
import { RuleRegistryPlace } from "@/types/rule-whitelists.ts";
import {
  getUnavailableRuleContextsForSubject,
  useRuleConditionTypes,
} from "@/queries/use-rules.query.ts";
import {
  ApiWorkflowBlockActionTask,
  ApiWorkflowBlockActionTaskSave,
} from "@/types/workflows/workflow.action-notification.types.ts";
import {
  ApiWorkflowBlockActionTypes,
  ApiWorkflowTriggerType,
} from "@/types/workflows/workflow.base-enum.types.ts";
import { ApiWorkflowBlock, ApiWorkflowVersion } from "@/types/workflows/workflow.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Input,
  Modal,
  ModalProps,
  Stack,
  Switch,
  TabItem,
  Tabs,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { WorkflowActionTimingEditor } from "@/pages/workflows/edit/actions/WorkflowActionTimingEditor.tsx";
import {
  getWorkflowActionTimingPayload,
  getWorkflowActionTimingValues,
  useWorkflows,
  WorkflowActionTimingFormFields,
} from "@/pages/workflows/workflow-hook.tsx";
import { useWorkflowPlaceholderOptions } from "@/pages/workflows/edit/actions/use-workflow-placeholder-options.ts";

interface WorkflowEditActionTaskFormData extends WorkflowActionTimingFormFields {
  title: string | null;
  task_title: string;
  description: string | null;
  assign_to_rule_groups: RuleGroup[];
  watcher_rule_groups: RuleGroup[];
  due_date_delay_days: number | null;
  reminders_enabled: boolean;
  reminder_settings: {
    channels: ApiNotificationChannelType[];
    mode: ReminderMode;
    custom_subject: string | null;
    custom_body: string | null;
    custom_frequency: number | null;
    custom_period: ReminderPeriod | null;
  };
}

export interface WorkflowEditActionTaskModalProps extends ModalProps {
  workflowVersion: ApiWorkflowVersion;
  block: ApiWorkflowBlock;
  actionTask: ApiWorkflowBlockActionTask;
}

export const WorkflowEditActionTaskModal = ({
  workflowVersion,
  block,
  actionTask,
  ...props
}: WorkflowEditActionTaskModalProps) => {
  const { t } = useTranslation();
  const { workflowActionTimingSchemaFields } = useWorkflows();
  const { close } = useModals();
  const { id } = { ...props };
  const { handleErrors } = useErrorHandle();
  const workflowTriggerType = workflowVersion.trigger_recurring_rule
    ? ApiWorkflowTriggerType.recurring
    : workflowVersion.trigger_schedule_rule
      ? ApiWorkflowTriggerType.scheduled
      : workflowVersion.trigger_event
        ? ApiWorkflowTriggerType.event
        : ApiWorkflowTriggerType.manual;
  const eventType = workflowVersion.trigger_event?.event_type;
  const placeholderOptions = useWorkflowPlaceholderOptions(
    eventType,
    workflowTriggerType === ApiWorkflowTriggerType.recurring
      ? ["assignee"]
      : ["employee", "assignee", "manager"]
  );
  const subjectUnavailableContexts = useMemo(
    () => getUnavailableRuleContextsForSubject(eventType),
    [eventType]
  );
  const assignToConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowTaskAssignTo,
    workflowTriggerType,
    subjectUnavailableContexts
  );
  const watcherConditionTypes = useRuleConditionTypes(
    RuleRegistryPlace.workflowTaskWatchers,
    workflowTriggerType,
    subjectUnavailableContexts
  );
  const getSupportedRuleCheck = (conditionTypeInfos: ApiRuleConditionTypeInfo[]) => {
    const conditionTypeInfoById = new Map(
      conditionTypeInfos.map((conditionType) => [conditionType.condition_type, conditionType])
    );

    return (rule: Rule) => {
      const conditionType = conditionTypeInfoById.get(rule.condition_type);

      return Boolean(
        conditionType &&
        (conditionType.context !== RuleConditionContext.affected_employee ||
          rule.sign === RuleApplySignType.include)
      );
    };
  };
  const isSupportedAssigneeRule = getSupportedRuleCheck(assignToConditionTypes.conditionTypeInfos);
  const isSupportedWatcherRule = getSupportedRuleCheck(watcherConditionTypes.conditionTypeInfos);

  const { conditions, handleAddCondition, handleEditCondition, handleDeleteCondition } =
    useWorkflowBlockConditions(workflowVersion, block);

  const [files, setFiles] = useState<ApiFile[]>(actionTask.files);
  async function removeFile(file: ApiFile) {
    setFiles((prev) => prev.filter((f) => f.id !== file.id));
    const { error } = await apiFiles.delete(file.uuid);
    if (error) handleErrors(error);
  }

  const defaultReminderSettings = {
    channels: [
      ApiNotificationChannelType.in_app,
      ApiNotificationChannelType.email,
      ApiNotificationChannelType.slack,
    ],
    mode: ReminderMode.medium,
    custom_subject: null,
    custom_body: null,
    custom_frequency: null,
    custom_period: null,
  };

  const WorkflowEditActionTaskSchemaObject = yup.object({
    title: yup.string().defined().nullable(),
    task_title: yup.string().required(t("errors:field_required")),
    description: yup.string().defined().nullable(),
    assign_to_rule_groups: workflowRuleGroupsSchema()
      .test(
        "supported-assign_to_rule_groups",
        t("errors:field_required"),
        (groups) =>
          assignToConditionTypes.isLoading ||
          groups.every((group) => group.rules.every(isSupportedAssigneeRule))
      )
      .test("non-empty-assignees", t("errors:field_required"), (groups) =>
        groups.some((group) => group.rules.length > 0)
      ),
    watcher_rule_groups: workflowRuleGroupsSchema().test(
      "supported-watcher_rule_groups",
      t("errors:field_required"),
      (groups) =>
        watcherConditionTypes.isLoading ||
        groups.every((group) => group.rules.every(isSupportedWatcherRule))
    ),
    due_date_delay_days: yup
      .number()
      .nullable()
      .defined()
      .transform((value, originalValue) => {
        if (originalValue === "" || originalValue == null || Number.isNaN(value)) {
          return null;
        }

        return value;
      })
      .typeError(t("errors:field_number_required"))
      .min(0, t("api_errors:minimum_value_must", { minimum: 0 })),
    ...workflowActionTimingSchemaFields,
    reminders_enabled: yup.boolean().required(),
    reminder_settings: yup.object({
      channels: yup
        .array()
        .of(
          yup
            .mixed<ApiNotificationChannelType>()
            .oneOf(Object.values(ApiNotificationChannelType))
            .required()
        )
        .default(defaultReminderSettings.channels),
      mode: yup
        .mixed<ReminderMode>()
        .oneOf(Object.values(ReminderMode))
        .required()
        .default(ReminderMode.medium),
      custom_subject: yup.string().nullable().default(null),
      custom_body: yup.string().nullable().default(null),
      custom_frequency: yup.number().nullable().default(null),
      custom_period: yup
        .mixed<ReminderPeriod>()
        .oneOf(Object.values(ReminderPeriod))
        .nullable()
        .default(null),
    }),
  });

  const {
    control,
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm<WorkflowEditActionTaskFormData>({
    mode: "onSubmit",
    resolver: yupResolver(WorkflowEditActionTaskSchemaObject),
    values: {
      title: block.title ?? "",
      ...getWorkflowActionTimingValues(block),
      task_title: actionTask.title,
      description: actionTask.description,
      assign_to_rule_groups: getWorkflowRuleGroups(
        actionTask.assign_to_rule_groups,
        actionTask.assign_to_rules,
        isSupportedAssigneeRule
      ),
      watcher_rule_groups: getWorkflowRuleGroups(
        actionTask.watcher_rule_groups,
        actionTask.watcher_rules,
        isSupportedWatcherRule
      ),
      due_date_delay_days: actionTask.due_date_delay_days,
      reminders_enabled: !!actionTask.reminder_settings?.mode,
      reminder_settings: actionTask.reminder_settings?.mode
        ? (actionTask.reminder_settings as WorkflowEditActionTaskFormData["reminder_settings"])
        : defaultReminderSettings,
    },
  });

  const watchedAssigneeGroups = useWatch({ control, name: "assign_to_rule_groups" });
  const watchedWatcherGroups = useWatch({ control, name: "watcher_rule_groups" });

  const dueDateDelayDays = useWatch({ control, name: "due_date_delay_days" });
  const hasDueDate =
    dueDateDelayDays !== null && dueDateDelayDays !== undefined && !isNaN(dueDateDelayDays);

  const updateBlockAction = useWorkflowUpdateBlockActionTask(control);

  const handleSave = handleSubmit(async (data) => {
    try {
      const hasDueDate =
        data.due_date_delay_days !== null &&
        data.due_date_delay_days !== undefined &&
        !isNaN(data.due_date_delay_days);

      const reminderSettings =
        data.reminders_enabled && data.reminder_settings
          ? {
              channels: data.reminder_settings.channels,
              custom_subject: data.reminder_settings.custom_subject,
              custom_body: data.reminder_settings.custom_body,
              mode: data.reminder_settings.mode,
              ...(data.reminder_settings.mode === ReminderMode.custom
                ? {
                    custom_frequency: data.reminder_settings.custom_frequency,
                    custom_period: data.reminder_settings.custom_period,
                  }
                : {}),
            }
          : null;

      const blockData: ApiWorkflowBlockActionTaskSave = {
        title: data.task_title,
        description: data.description,
        assign_to_rule_groups: toWorkflowRuleGroupsPayload(data.assign_to_rule_groups),
        watcher_rule_groups: toWorkflowRuleGroupsPayload(data.watcher_rule_groups),
        use_due_date: hasDueDate,
        due_date_delay_days: hasDueDate ? (data.due_date_delay_days ?? null) : null,
        file_uuids: files.map((f) => f.uuid),
        reminder_settings: reminderSettings,
      };

      await updateBlockAction.mutateAsync({
        workflowId: workflowVersion.workflow_id,
        workflowVersionId: workflowVersion.id,
        workflowBlockId: block.id,
        payload: {
          title: data.title,
          ...getWorkflowActionTimingPayload(data),
          block_data: blockData,
          conditions: conditions.map((x) => ({ ...x })),
        },
      });

      analyticsService.trackEvent(analyticEvents.workflows.blockEdited, {
        [analyticProperties.id]: workflowVersion.workflow_id,
        [analyticProperties.actionType]: ApiWorkflowBlockActionTypes.task,
      });

      close(id);
    } catch {}
  });

  const [activeTab, setActiveTab] = useState(0);

  return (
    <>
      <Modal
        {...props}
        variant={"side"}
        title={t("workflow:action_type_task")}
        layout={"stretch"}
        withCloser
        closeByEscEnabled
        size={"lg"}
        actions={
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              size={"lg"}
              className={"w-full"}
              type={"submit"}
              onClick={handleSave}
              disabled={assignToConditionTypes.isLoading || watcherConditionTypes.isLoading}
              isLoading={updateBlockAction.isPending}
            >
              {t("common:save")}
            </Button>
          </Stack>
        }
      >
        <form onSubmit={handleSave}>
          <Tabs onActiveTabChange={setActiveTab} activeTab={activeTab}>
            <TabItem title={t("tasks:task")}>
              <Stack className={"py-6"} gap={"lg"}>
                <InputWithVariableInsert
                  {...register("task_title")}
                  error={errors.task_title?.message}
                  label={t("common:title")}
                  placeholder={t("tasks:title_placeholder")}
                  required
                  variableOptions={placeholderOptions}
                />

                <Stack gap={"md"}>
                  <WorkflowReminderSettingsSection
                    control={control}
                    setValue={setValue}
                    hasSmartBase={hasDueDate}
                    smartBaseValue={dueDateDelayDays ?? 0}
                    requiredText={t("core:reminder_requires_due_date_days")}
                    renderHeader={({ checked, onChange }) => (
                      <Stack direction={"horizontal"} gap={"md"} items={"center"}>
                        <Box className={"grow"}>
                          <Input
                            label={t("workflow:action_type_task_due_date_delay_days")}
                            type="number"
                            min={0}
                            {...register("due_date_delay_days", {
                              setValueAs: (value) =>
                                value === "" || value == null ? null : Number(value),
                            })}
                            error={errors.due_date_delay_days?.message}
                          />
                        </Box>
                        <Switch
                          size={"sm"}
                          checked={checked}
                          onChange={onChange}
                          title={t("core:turn_on_reminders")}
                          description={t("tasks:overdue_alerts_sent_anyway")}
                        />
                      </Stack>
                    )}
                  />
                </Stack>

                <Stack>
                  <Title header={t("common:assign_to")} paddingBottom size={"xs"} />
                  <RuleGroupsEditor
                    control={control}
                    setValue={setValue}
                    fieldName={"assign_to_rule_groups"}
                    place={RuleRegistryPlace.workflowTaskAssignTo}
                    countEmployees={canPreviewWorkflowRuleGroups(
                      watchedAssigneeGroups,
                      assignToConditionTypes.conditionTypeInfos
                    )}
                    error={errors.assign_to_rule_groups?.message}
                    conditionTypes={assignToConditionTypes.conditionTypes}
                    singleSelectConditionTypes={[RuleConditionType.employee]}
                    variant={"list"}
                  />
                </Stack>

                <Stack>
                  <Title header={t("tasks:watchers")} paddingBottom size={"xs"} />
                  <RuleGroupsEditor
                    control={control}
                    setValue={setValue}
                    fieldName={"watcher_rule_groups"}
                    place={RuleRegistryPlace.workflowTaskWatchers}
                    countEmployees={canPreviewWorkflowRuleGroups(
                      watchedWatcherGroups,
                      watcherConditionTypes.conditionTypeInfos
                    )}
                    error={errors.watcher_rule_groups?.message}
                    conditionTypes={watcherConditionTypes.conditionTypes}
                    singleSelectConditionTypes={[RuleConditionType.employee]}
                    variant={"list"}
                  />
                </Stack>

                <Stack>
                  <Title header={t("common:description")} size={"xs"} paddingBottom />
                  <Controller
                    control={control}
                    name="description"
                    render={({ field: { value, onChange } }) => (
                      <RichEditor
                        value={value || ""}
                        onChange={onChange}
                        error={errors.description?.message}
                        minHeight={100}
                        placeholder={t("tasks:description_placeholder")}
                        placeholderOptions={placeholderOptions}
                      />
                    )}
                  />
                  <Stack gap={"sm"} items={"start"} className={"mt-2"}>
                    {files.length > 0 && (
                      <Stack gap={"sm"} className={"w-full min-w-0"}>
                        {files.map((file) => (
                          <FileItem
                            key={`file${file.id}`}
                            file={file}
                            onRemove={() => removeFile(file)}
                          />
                        ))}
                      </Stack>
                    )}
                    <Stack items={"start"}>
                      <Box>
                        <FileUpload
                          entityId={block.id}
                          entityType={ApiEntityTypes.other}
                          afterSend={(file) => {
                            if (file) setFiles((prev) => [...prev, file]);
                          }}
                          buttonText={t("common:attach")}
                        />
                      </Box>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </TabItem>
            <TabItem title={t("workflow:conditions")}>
              <Stack gap={"lg"} className={"py-6"}>
                <Stack>
                  <Title header={t("workflow:block")} size={"xs"} />
                  <Input
                    label={t("common:title")}
                    type="text"
                    {...register("title")}
                    error={errors.title?.message}
                  />
                </Stack>
                <WorkflowActionTimingEditor
                  control={control}
                  register={register}
                  errors={errors}
                  triggerDateAvailable={workflowVersion.trigger_schedule_rule !== null}
                />
                <WorkflowBlockConditionsList
                  conditions={conditions}
                  onAdd={handleAddCondition}
                  onEdit={handleEditCondition}
                  onDelete={handleDeleteCondition}
                />
              </Stack>
            </TabItem>
          </Tabs>
        </form>
      </Modal>
    </>
  );
};
